import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import {
  CARER_ROLE,
  COORDINATOR_ROLE,
  CUSTOMER_MEMBER_ROLE,
  CUSTOMER_SELF_ROLE,
  DASHBOARD_ROLES,
  FILE_UPLOAD_MAX_SIZE,
  GALLERY,
  IMAGE_FILE_TYPES,
  NOTE,
  ORGANISATION_ADMIN_ROLE,
  SEVERE,
  VIDEO,
} from '@careapp-group/core';
import { AIService, CustomerDto, FileRecordDto, MemoResponseDto } from '@careapp-group/ng-sdk';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import {
  defaultIfEmpty,
  distinctUntilChanged,
  filter,
  map,
  pluck,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import * as actions from '../../../store/actions';
import { RequestFileUploadRetryAction } from '../../../store/file-upload/file-upload.actions';
import { MultiCustomerContentCreation } from '../../customer-content-creation/customer-content-creation';
import { FileIconComponent } from '../../file-icon/file-icon.component';
import { requiredAndNotEmpty } from '../../validators';
import { MEMOS, MEMO_CATEGORIES } from '../../../store';
import { getSelfCustomer } from '../../../store/customers/customers.selectors';
import {
  getFileUploadProgress,
  getFileUploadError,
  getFileUploadSuccess,
  getFileUploadResponse,
  getFilesUploading,
} from '../../../store/file-upload/file-upload-selectors';
import * as selectors from '../../../store/reducers';
import { getIsFamily } from '../../../store/organisations/organisations.selectors';
import { environment } from '../../../../environments/environment';
import { VideoProcessingService } from '../../../organisation/create/video-processing.service';
import { allowPhotos, FileDownloadUrlPipeV2 } from '../../pipes';
import { canUseLists } from '../../../store/customer-lists/customer-lists.selectors';
import {
  AiSuggestionDialogComponent,
  AiSuggestionDialogData,
} from '../../ai-suggestion-dialog/ai-suggestion-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { WINDOW } from '../../../app.di';

interface MediaItem {
  id: string;
  url?: string;
  userFile?: File;
  fileRecord?: FileRecordDto;
  progress$: Observable<number>;
  hasError$: Observable<boolean>;
  errorMessage$: Observable<string>;
  type?: string;
}

@Component({
  selector: 'ca-memo-form',
  templateUrl: './memo-form.component.html',
  styleUrls: ['./memo-form.component.scss'],
  standalone: false,
})
export class MemoFormComponent extends MultiCustomerContentCreation implements OnInit, OnDestroy {
  public isFamily$: Observable<boolean>;
  public canUseLists$: Observable<boolean>;

  get customerListIds(): string[] {
    return this.form.get('customerListIds').value;
  }
  set customerListIds(customerListIds: string[]) {
    this.form.patchValue({
      customerListIds,
    });
    this.changeDetector.detectChanges();
  }

  readonly MAX_AVATAR_COUNT = 3;
  IMAGE_TYPES = IMAGE_FILE_TYPES.join(',');

  public get mediaItems() {
    return this._mediaItems;
  }
  public set mediaItems(data) {
    if (!data) {
      return;
    }
    this._mediaItems = data;
    this._updateFileIds();
  }

  get customerIds() {
    return this.form.get('customerIds').value;
  }

  set customerIds(customerIds: string[]) {
    this.form.patchValue({
      customerIds,
    });
    this.changeDetector.detectChanges();
  }

  get options() {
    const type = this.form.get('type').value;
    return {
      ...super.options,
      disableNoMedia: type === GALLERY || type === VIDEO,
    };
  }

  constructor(
    private changeDetector: ChangeDetectorRef,
    protected dialog: MatDialog,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    protected store: Store<any>,
    private video: VideoProcessingService,
    protected viewContainerRef: ViewContainerRef,
    public downloadUrlPipe: FileDownloadUrlPipeV2,
    private aiService: AIService,
    private translateService: TranslateService,
    @Inject(WINDOW) private window: Window & typeof globalThis,
  ) {
    super(dialog, store, viewContainerRef, route.snapshot.queryParamMap.get('customerId'));
  }
  public types = {
    NOTE: NOTE,
    GALLERY: GALLERY,
    VIDEO: VIDEO,
  };
  public typeSubscription: Subscription;
  public mustShareSubscription: Subscription;
  public title$: Observable<string>;

  public SEVERE = SEVERE;

  _mediaItems: MediaItem[] = [];

  public selected: string;

  public form: UntypedFormGroup;

  public loading$: Observable<any>;
  public memoCategories$: Observable<any>;
  public memoCategoriesLoading$: Observable<boolean>;
  public organisationPreferences$: Observable<any>;
  public customerSelf$: Observable<CustomerDto>;
  public showShareOption$: Observable<boolean>;
  public mustShare$: Observable<boolean>;
  private _existingMemo: MemoResponseDto;

  @Input() tenantId: string;

  @Input() set existingMemo(message: MemoResponseDto) {
    if (!message) {
      return;
    }
    this._existingMemo = message;

    this.store.dispatch(actions.Customers.RequestAllAction());
    this.store.dispatch(actions.MemoCategories.RequestAllAction());

    // Retrieve all customers in the provided memo.
    for (const customerId of message.customerIds ?? []) {
      this.store.dispatch(actions.Customers.RequestByIdAction({ id: customerId }));
    }

    this.initialiseFormIfNull();

    this.form.patchValue({
      customerListIds: [],
      customerIds: message.customerIds || [],
      type: message.type,
      body: message.shared ? message.sharedBody : message.body,
      shared: message.shared || false,
      sensitive: message.sensitive || false,
      categoryIds: message.categories?.map((c) => c.id) || [],
      fileIds: message.media?.map((m) => m.id) || [],
    });

    if (message.type === GALLERY || message.type === VIDEO) {
      this.form.get('fileIds').setValidators([requiredAndNotEmpty]);
      this.form.get('fileIds').updateValueAndValidity();
    } else {
      this.form.get('fileIds').setValidators([]);
    }

    const mediaItems: MediaItem[] = [];
    message.media.forEach((fileRecord) => {
      this.downloadUrlPipe.getDownloadUrl(fileRecord).subscribe((url) => {
        mediaItems.push({
          fileRecord: fileRecord,
          id: fileRecord.id,
          progress$: of(100),
          hasError$: of(false),
          errorMessage$: of(''),
          url,
        });

        if (mediaItems.length === message.media.length) {
          this.mediaItems = mediaItems;
          this.changeDetector.detectChanges(); // Ensure change detection runs
        }
      });
    });

    this.changeDetector.detectChanges();

    this.setInitialCustomers(message.customerIds ?? []);
    this.changeDetector.detectChanges();
  }

  get existingMemo() {
    return this._existingMemo;
  }

  @Input()
  submissionEnabled: boolean = true;

  @Output()
  onSubmit = new EventEmitter<{
    customerListIds: string[];
    customerIds: string[];
    type: string;
    body: string;
    shared: boolean;
    sensitive: boolean;
    categoryIds: string[];
    fileIds: string[];
  }>();

  @ViewChild('confirmationDialog', { static: true, read: TemplateRef })
  confirmationDialog: TemplateRef<any>;

  DASHBOARD_ROLES = DASHBOARD_ROLES;
  DASHBOARD_AND_CUSTOMER_ROLES = [...DASHBOARD_ROLES, CUSTOMER_MEMBER_ROLE];

  ngOnInit() {
    const initialId = this.route.snapshot.queryParamMap.get('customerId');
    this.initialiseFormIfNull();
    this.form.patchValue({ customerIds: initialId ? [initialId] : [] });
    this.typeSubscription = this.route.data
      .pipe(pluck('type'), defaultIfEmpty(NOTE))
      .subscribe((type) => {
        this.form.patchValue({ type: type });
        if (type === GALLERY || type === VIDEO) {
          this.form.get('fileIds').setValidators([requiredAndNotEmpty]);
          this.form.get('fileIds').updateValueAndValidity();
        } else {
          this.form.get('fileIds').setValidators([]);
        }
      });
    this.title$ = this.route.data.pipe(pluck('title'));

    this.loading$ = combineLatest([
      this.store.select(selectors.getCreating(MEMOS)),
      this.store.select(getFilesUploading),
    ]).pipe(map(([memoCreating, filesUploading]) => memoCreating || filesUploading));
    this.customerSelf$ = this.store.select(getSelfCustomer).pipe(filter((customer) => !!customer));
    this.memoCategories$ = this.store.select(selectors.getCurrentOrganisationCategories);
    this.memoCategoriesLoading$ = this.store.select(selectors.getLoadingAll(MEMO_CATEGORIES));
    // Ensure we get the current organisation's categories
    this.store.dispatch(actions.MemoCategories.RequestByIdAction({ id: '' }));
    this.organisationPreferences$ = this.store.select(selectors.org.currentOrgPreferences);
    this.isFamily$ = this.store.select(getIsFamily);
    this.canUseLists$ = this.store.select(canUseLists);

    this.mustShare$ = this.customers$.pipe(
      withLatestFrom(this.store.select(selectors.org.currentOrgRoles)),
      map(([customers, roles]) => {
        const sharingStaffRoles = [ORGANISATION_ADMIN_ROLE, COORDINATOR_ROLE];
        // sharingStaffRoles get the option of choosing to share
        if (roles.some((r) => sharingStaffRoles.includes(r))) {
          return false;
          // Team members do not share, and do not have a choice
        } else if (roles.some((r) => r === CARER_ROLE)) {
          return false;
        } else {
          // Customer users get to choose whether to share posts that are only about
          // them, but must share posts that are about other customers (ie they are followers)
          return !customers.every((customer) =>
            customer.permissions?.some((p) => p.role === CUSTOMER_SELF_ROLE),
          );
        }
      }),
    );

    this.showShareOption$ = this.customers$.pipe(
      withLatestFrom(this.store.select(selectors.org.currentOrgRoles)),
      map(([customers, roles]) => {
        const sharingStaffRoles = [ORGANISATION_ADMIN_ROLE, COORDINATOR_ROLE];
        if (roles.some((r) => sharingStaffRoles.includes(r))) {
          return true;
        } else {
          return customers.every((customer) =>
            customer.permissions?.some((p) => p.role === CUSTOMER_SELF_ROLE),
          );
        }
      }),
    );

    this.mustShareSubscription = this.mustShare$
      .pipe(distinctUntilChanged())
      .subscribe((mustShare) => {
        if (mustShare) {
          this.form.patchValue({ shared: true });
        }
      });
  }

  getAiSuggestion() {
    const dialogRef = this.dialog.open(AiSuggestionDialogComponent, {
      width: '400px',
      data: {
        fields: null,
        loading: true,
        error: null,
      } as AiSuggestionDialogData,
    });

    this.aiService
      .correctMemo({
        aiCorrectMemoRequestDto: {
          input: this.form.value.body,
          customerIds: this.form.value.customerIds,
          tenantId: this.tenantId,
        },
      })
      .subscribe({
        next: (suggestion) => {
          dialogRef.componentInstance.data = {
            fields: [{ title: 'Body', value: suggestion.suggestion }],
            loading: false,
            error: null,
          };
        },
        error: (error: { error: { reason?: string; status?: string }; status?: number }) => {
          let errorMessage = 'Could not make a suggestion at this time';
          // For BadRequest errors, we provide a reason and status text.
          if (error.status === 400 && error.error.status != null) {
            const statusCode = error.error.status;
            errorMessage = this.translateService.instant(`ai.polishStatusCode.${statusCode}`);
          }

          dialogRef.componentInstance.data = {
            fields: [{ title: 'Body', value: null }],
            loading: false,
            error: errorMessage,
          };
        },
      });

    dialogRef.afterClosed().subscribe((accepted) => {
      if (accepted && dialogRef.componentInstance.data.fields?.[0]?.value) {
        this.updateBody(dialogRef.componentInstance.data.fields?.[0]?.value);
      }
    });
  }

  updateBody(suggestion?: string) {
    if (suggestion) {
      this.form.patchValue({
        body: suggestion,
      });
    }
  }

  // If no form exists, will create the form. Otherwise, leaves as is.
  initialiseFormIfNull() {
    if (!this.form) {
      this.form = this.formBuilder.group({
        customerListIds: this.formBuilder.control([]),
        customerIds: this.formBuilder.control([], requiredAndNotEmpty),
        type: this.formBuilder.control(NOTE, requiredAndNotEmpty),
        body: this.formBuilder.control(null, requiredAndNotEmpty),
        shared: this.formBuilder.control(false),
        sensitive: this.formBuilder.control(false),
        categoryIds: this.formBuilder.control([], requiredAndNotEmpty),
        fileIds: this.formBuilder.control([]),
      });
    }
  }

  ngOnDestroy() {
    if (this.mustShareSubscription) {
      this.mustShareSubscription.unsubscribe();
    }
    if (this.typeSubscription) {
      this.typeSubscription.unsubscribe();
      this.typeSubscription = undefined;
    }
    // Release any temporary blob urls created
    this.mediaItems.forEach((item) => {
      if (item.url && /^(blob:)/.test(item.url)) {
        this.window.URL.revokeObjectURL(item.url);
      }
    });
  }

  async submitMemo() {
    const memo = { ...this.form.value };
    const isGalleryOrVideo = ![GALLERY, VIDEO].includes(memo.type);
    let customers = await this.customers$.pipe(take(1)).toPromise();

    if (isGalleryOrVideo) {
      customers = customers.filter((customer) => customer.settings?.allowMemoImages !== false);
    }

    if ((customers && !allowPhotos(customers)) || isGalleryOrVideo) {
      memo.fileIds = [];
    }
    this.onSubmit.emit(this.form.value);
  }

  typeChanged(type) {
    this.form.patchValue({
      type,
    });

    if (type !== SEVERE) {
      return this.form.patchValue({ confirmSevereConcern: true });
    }

    if (type === SEVERE) {
      return this.form.patchValue({ confirmSevereConcern: false });
    }
  }

  categoriesChanged(values) {
    this.form.patchValue({
      categoryIds: values,
    });
  }

  addFile({ file, url, type }: { file: File; url: string; type: 'image' | 'video' }) {
    if (!this._validateFileSize(file)) {
      return;
    }

    const id = (((1 + Math.random()) * 0x10000) | 0).toString(16); // Random 5 character hex string

    this.store.dispatch(actions.RequestFileUploadStartAction({ id, file }));

    const progress$ = combineLatest([
      this.store.select(getFileUploadProgress(id)),
      this.store.select(getFileUploadError(id)),
      this.store.select(getFileUploadSuccess(id)),
    ]).pipe(
      map(([progress, error, success]) => {
        if (error) {
          return 0;
        }
        if (success) {
          return 100;
        }
        return progress || 0;
      }),
    );
    const hasError$ = this.store.select(getFileUploadError(id)).pipe(map((error) => !!error));
    const errorMessage$ = this.store
      .select(getFileUploadError(id))
      .pipe(map((error) => error || ''));

    const mediaItem: MediaItem = {
      id,
      userFile: file,
      type,
      url,
      progress$,
      hasError$,
      errorMessage$,
    };

    this.store
      .select(getFileUploadSuccess(id))
      .pipe(
        filter((success) => success === true),
        take(1),
      )
      .subscribe(() => {
        this._handleUploadSuccess(id);
      });

    this.mediaItems = [...this._mediaItems, mediaItem];
    this.changeDetector.detectChanges();
  }

  _handleUploadSuccess(id: string): void {
    this.store
      .select(getFileUploadResponse(id))
      .pipe(
        filter((response) => !!response),
        take(1),
      )
      .subscribe((response) => {
        // Update the media item with the server response
        const itemIndex = this._mediaItems.findIndex((item) => item.id === id);
        if (itemIndex >= 0) {
          this._mediaItems[itemIndex].fileRecord = response;
          this._updateFileIds();
        }
      });
  }

  addPhotos(files: File[]) {
    for (let file of files) {
      this.addFile({
        file,
        url: this.window.URL.createObjectURL(file),
        type: 'image',
      });
    }
  }

  async addVideo(videos: File[]) {
    if (videos.length !== 1) {
      return;
    }
    const video = videos[0];
    const duration = await this.video.getVideoDuration(video);
    if (duration > environment.maxVideoUploadLengthS) {
      this.store.dispatch(
        actions.ShowSnackBar({
          message: `Video too long - must be less than ${
            environment.maxVideoUploadLengthS / 60.0
          } minutes`,
        }),
      );
      return;
    }

    let thumbnail: string;
    try {
      thumbnail = await this.video.generateThumbnail(video, {
        width: 150,
        height: 150,
      });
    } catch (err) {
      thumbnail = FileIconComponent.iconUrlForMimeType(video.type);
    }
    this.addFile({
      file: video,
      url: thumbnail,
      type: 'video',
    });
  }

  removeMedia(id: string) {
    this.mediaItems = this._mediaItems.filter((item) => item.id !== id);
    this._updateFileIds();
    this.changeDetector.detectChanges();
  }

  async promptRemove(id: string) {
    const dialogRef = this.dialog.open(this.confirmationDialog);
    const result = await dialogRef.afterClosed().toPromise();
    if (result) {
      this.removeMedia(id);
    }
  }

  retryUpload(id: string) {
    this.store.dispatch(RequestFileUploadRetryAction({ id }));
  }

  _validateFileSize(file: File): boolean {
    if (file.size >= FILE_UPLOAD_MAX_SIZE) {
      const maxSize = Math.floor(FILE_UPLOAD_MAX_SIZE / 1024 / 1024);
      this.store.dispatch(
        actions.ShowSnackBar({
          message: `Selected file was too large - maximum size is ${maxSize}MB`,
        }),
      );
      return false;
    }
    return true;
  }

  _updateFileIds() {
    // Filter out files that have not been uploaded yet, patch form with remaining IDs.
    const validFileIds = this._mediaItems
      .filter((item) => {
        return item.fileRecord?.id;
      })
      .map((item) => item.fileRecord!.id);

    this.form.patchValue({
      fileIds: validFileIds,
    });

    this.form.get('fileIds').updateValueAndValidity();
  }
}
